import React from 'react';
import { Leg } from 'acscp/store';
import style from './legTable.module.scss';
import { FormattedMessage } from 'react-intl';
import { LabeledItem } from 'acscp/components/common/labeledItem';
import { addZeroWidthSpacesAroundSeparatorCharacters } from 'acscp/components/common/helpers/stringHelpers';
import { LocalTimeWithFallback } from 'acscp/components/common/localTimeWithFallback';
import { DateStyle } from 'acscp/components/common/formattedDate';
import { TimeType } from 'acscp/api/models/customTypes/timeType';
import { FormattedDateRange } from 'acscp/components/common/formattedDateRange';
import { Department } from 'acscp/api/models/customTypes/department';

const LegTable: React.SFC<{legs: Leg[], departmentKey: Department}> = ({legs, departmentKey}) => {
    const shouldShowPetsColumn = departmentKey === Department.PJETS && legs.some(leg => leg.numberOfPets && leg.numberOfPets > 0);
    return (
        <div>
            <table className={style.LegTable}>
                <tbody>
                    <tr>
                        <th>
                            <FormattedMessage
                                id={'components.legs.legTable.flight'}
                                defaultMessage={'FLIGHT'}
                            />
                        </th>
                        <th>
                            <FormattedMessage
                                id={'components.legs.legTable.date'}
                                defaultMessage={'DATE'}
                            />
                        </th>
                        <th>
                            {legs.length > 0 && legs[0].departureTimeType === TimeType.UTC
                                ? <FormattedMessage
                                    id={'components.legs.legTable.departUTC'}
                                    defaultMessage={'REQUESTED DEPARTURE (UTC)'}
                                />
                                : <FormattedMessage
                                    id={'components.legs.legTable.departLocal'}
                                    defaultMessage={'REQUESTED DEPARTURE (LOCAL)'}
                                />
                            }
                        </th>
                        {departmentKey !== Department.Cargo &&
                            <th>
                                <FormattedMessage
                                    id={'components.legs.legTable.passengers'}
                                    defaultMessage={'PASSENGERS'}
                                />
                            </th>
                        }
                        {departmentKey === Department.Cargo &&
                            <th>
                                <FormattedMessage
                                    id={'components.legs.legTable.cargoWeight'}
                                    defaultMessage={'CARGO WEIGHT'}
                                />
                            </th>
                        }
                        {departmentKey === Department.PJETS && shouldShowPetsColumn &&
                            <th>
                                <FormattedMessage
                                    id={'components.legs.legTable.pets'}
                                    defaultMessage={'PETS'}
                                />
                            </th>
                        }
                    </tr>
                    {legs.map((leg, index) => (LegRow(leg, index, departmentKey, shouldShowPetsColumn)))}
                </tbody>
            </table>
            <div className={style.MobileView}>
                    {legs.map((leg, index) => (LegSection(leg, index, departmentKey, shouldShowPetsColumn)))}
            </div>
        </div>
    );
};

function LegRow(leg: Leg, index: number, departmentKey: Department, shouldShowPetsColumn: boolean): JSX.Element {
    return (
        <tr key={index}>
            <td>
                {addZeroWidthSpacesAroundSeparatorCharacters(leg.departureLocation)}
                {' - '}
                {addZeroWidthSpacesAroundSeparatorCharacters(leg.arrivalLocation)}
            </td>
            <td>
                <FormattedDateRange date={leg.departureDate} rangeEndDate={leg.departureRangeEndDate} style={DateStyle.Long} />
            </td>
            <td>
                <LocalTimeWithFallback dateTime={leg.departureDate} />
            </td>
            {departmentKey !== Department.Cargo &&
                <td>{leg.numberOfPassengers}</td>
            }
            {departmentKey === Department.Cargo &&
                <td>{leg.cargoWeight}</td>
            }
            {departmentKey === Department.PJETS && shouldShowPetsColumn &&
                <td>{leg.numberOfPets}</td>
            }
        </tr>
    );
}

function LegSection(leg: Leg, index: number, departmentKey: Department, shouldShowPetsColumn: boolean): JSX.Element {
    return (
        <div className={style.LegSection} key={index}>
            <LabeledItem
                label={
                    <FormattedMessage
                        id={'components.legs.legTable.flightMobile'}
                        defaultMessage={'Flight'}
                    />
                }
            >
                {addZeroWidthSpacesAroundSeparatorCharacters(leg.departureLocation)}
                {' - '}
                {addZeroWidthSpacesAroundSeparatorCharacters(leg.arrivalLocation)}
            </LabeledItem>
            <LabeledItem
                label={
                    <FormattedMessage
                        id={'components.legs.legTable.dateMobile'}
                        defaultMessage={'Date'}
                    />
                }
            >
                <FormattedDateRange date={leg.departureDate} rangeEndDate={leg.departureRangeEndDate} style={DateStyle.Long} />
            </LabeledItem>
            <LabeledItem
                label={leg.departureTimeType === TimeType.UTC
                    ? <FormattedMessage
                        id={'components.legs.legTable.departUTCMobile'}
                        defaultMessage={'Requested departure (UTC)'}
                    />
                    : <FormattedMessage
                        id={'components.legs.legTable.departLocalMobile'}
                        defaultMessage={'Requested departure (local)'}
                    />
                }
            >
                <LocalTimeWithFallback dateTime={leg.departureDate} />
            </LabeledItem>
            {departmentKey !== Department.Cargo &&
                <LabeledItem
                    label={
                        <FormattedMessage
                            id={'components.legs.legTable.passengersMobile'}
                            defaultMessage={'Passengers'}
                        />
                    }
                >
                    {leg.numberOfPassengers}
                </LabeledItem>
            }
            {departmentKey === Department.Cargo &&
                <LabeledItem
                    label={
                        <FormattedMessage
                            id={'components.legs.legTable.cargoWeightMobile'}
                            defaultMessage={'Cargo Weight'}
                        />
                    }
                >
                    {leg.cargoWeight}
                </LabeledItem>
            }
            {shouldShowPetsColumn &&
                <LabeledItem
                    label={
                        <FormattedMessage
                            id={'components.legs.legTable.petsMobile'}
                            defaultMessage={'Pets'}
                        />
                    }
                >
                    {leg.numberOfPets}
                </LabeledItem>
            }
        </div>
    );
}

export { LegTable };
